import type {
  Basket,
  Contact,
  SelectionValues,
  TierAvailability,
} from "./booking-api";
import {
  BookingError,
  checkoutUrl,
  fetchAvailability,
  registerInterest,
  selection,
  startCheckout,
} from "./booking-api";
import type { BookingConfig } from "./booking-config";
import { CONTACT_EMAIL, loadBookingConfig } from "./booking-config";
import { control, element, prefersReducedMotion } from "./dom";

const SUBMIT_LABEL = "Continue to secure payment ↗";
const INTEREST_SUBMIT_LABEL = "Register my interest ↗";
const NOTE_PAYMENT =
  "Your confirmation and receipt will be sent by email. Secure payment opens in Stripe, then returns here.";
const NOTE_INTEREST =
  "No payment now. We will email you, pair you with others on a Kund and hold a place for you.";
const FEW_LEFT = 5;

interface Copy {
  eyebrow: string;
  body: string;
}

const BOOKED: Copy = {
  eyebrow: "YOUR SEVA IS RECEIVED",
  body: "Thank you. Your Kund is booked. We have emailed your confirmation and receipt, and we look forward to sitting with you.",
};

function interestReceived(email: string): Copy {
  return {
    eyebrow: "INTEREST RECEIVED",
    body: `Thank you. Each Kund seats a small group together, so we will pair you with others and hold a place for you. Someone will be in touch soon. We have sent a note to ${email}.`,
  };
}

interface Form {
  form: HTMLFormElement;
  fields: HTMLFieldSetElement;
  groupSize: HTMLSelectElement;
  tiers: HTMLDivElement;
  extras: HTMLDivElement;
  priced: HTMLDivElement;
  interestOffer: HTMLParagraphElement;
  interestToggle: HTMLButtonElement;
  donation: HTMLInputElement;
  name: HTMLInputElement;
  email: HTMLInputElement;
  phone: HTMLInputElement;
  heardAboutUs: HTMLSelectElement;
  selectionStatus: HTMLParagraphElement;
  summary: HTMLDivElement;
  total: HTMLElement;
  submit: HTMLButtonElement;
  note: HTMLParagraphElement;
  status: HTMLParagraphElement;
  retry: HTMLButtonElement;
  received: HTMLDivElement;
  receivedEyebrow: HTMLParagraphElement;
  receivedCopy: HTMLParagraphElement;
}

function findForm(): Form {
  const form = element("booking-form", HTMLFormElement);
  return {
    form,
    fields: element("booking-fields", HTMLFieldSetElement),
    groupSize: control(form, "groupSize", HTMLSelectElement),
    tiers: element("booking-tiers", HTMLDivElement),
    extras: element("booking-extras", HTMLDivElement),
    priced: element("booking-priced", HTMLDivElement),
    interestOffer: element("interest-offer", HTMLParagraphElement),
    interestToggle: element("interest-toggle", HTMLButtonElement),
    donation: control(form, "donation", HTMLInputElement),
    name: control(form, "name", HTMLInputElement),
    email: control(form, "email", HTMLInputElement),
    phone: control(form, "phone", HTMLInputElement),
    heardAboutUs: control(form, "heardAboutUs", HTMLSelectElement),
    selectionStatus: element("selection-status", HTMLParagraphElement),
    summary: element("booking-summary", HTMLDivElement),
    total: element("booking-total", HTMLElement),
    submit: element("booking-submit", HTMLButtonElement),
    note: element("booking-note", HTMLParagraphElement),
    status: element("booking-status", HTMLParagraphElement),
    retry: element("booking-retry", HTMLButtonElement),
    received: element("booking-received", HTMLDivElement),
    receivedEyebrow: element("received-eyebrow", HTMLParagraphElement),
    receivedCopy: element("received-copy", HTMLParagraphElement),
  };
}

function labelled(text: string, input: HTMLElement): HTMLLabelElement {
  const wrapper = document.createElement("label");
  const caption = document.createElement("span");
  caption.textContent = text;
  wrapper.append(caption, input);
  return wrapper;
}

function scrollToRegister(): void {
  document.getElementById("register")?.scrollIntoView({
    behavior: prefersReducedMotion() ? "auto" : "smooth",
    block: "start",
  });
}

class Booking {
  private readonly ui: Form;
  private readonly config: BookingConfig;
  private readonly money: Intl.NumberFormat;
  private readonly tierInputs: HTMLInputElement[] = [];
  private readonly addOnInputs: HTMLInputElement[] = [];
  private availability: TierAvailability[] | undefined;
  private busy = false;
  private refreshing = false;
  private interestMode = false;

  constructor(ui: Form, config: BookingConfig) {
    this.ui = ui;
    this.config = config;
    this.money = new Intl.NumberFormat("en-AU", {
      style: "currency",
      currency: config.event.price.currency.toUpperCase(),
    });
  }

  /** Builds the controls from the shared catalog, then checks live availability. */
  async start(): Promise<void> {
    const { ui, config } = this;
    const price = config.event.price;
    const maxSeats = price.tiers.reduce((max, t) => Math.max(max, t.seats), 1);
    for (let size = 1; size <= maxSeats; size += 1) {
      ui.groupSize.add(
        new Option(size === 1 ? "1 person" : `${size} people`, String(size)),
      );
    }
    ui.groupSize.value = "2";

    for (const tier of price.tiers) {
      const label = document.createElement("label");
      label.className = "tier";
      const input = document.createElement("input");
      input.type = "radio";
      input.name = "tierId";
      input.value = tier.id;
      input.required = true;
      const copy = document.createElement("span");
      const title = document.createElement("strong");
      title.textContent = tier.label;
      const status = document.createElement("small");
      status.className = "tier-status";
      const amount = document.createElement("span");
      amount.className = "tier-price";
      amount.textContent = this.money.format(tier.amountCents / 100);
      copy.append(title, status);
      label.append(input, copy, amount);
      ui.tiers.append(label);
      this.tierInputs.push(input);
    }

    for (const addOn of price.addOns ?? []) {
      const input = document.createElement("input");
      input.type = "number";
      input.name = addOn.id;
      input.min = "0";
      input.max = String(addOn.maxQuantity ?? 10);
      input.step = "1";
      input.value = "0";
      input.inputMode = "numeric";
      ui.extras.prepend(
        labelled(
          `${addOn.label} · ${this.money.format(addOn.amountCents / 100)} each`,
          input,
        ),
      );
      this.addOnInputs.push(input);
    }

    ui.donation.max = String(config.donation.maxCents / 100);
    for (const referral of config.referrals) {
      ui.heardAboutUs.add(new Option(referral.label, referral.value));
    }

    ui.form.addEventListener("input", () => this.update());
    ui.groupSize.addEventListener("change", () => this.groupChanged());
    ui.interestToggle.addEventListener("click", () => {
      this.setInterestMode(!this.interestMode);
    });
    ui.retry.addEventListener("click", () => void this.refresh());
    window.addEventListener("focus", () => void this.refresh());
    ui.form.addEventListener("submit", (event) => {
      event.preventDefault();
      void this.submit();
    });

    ui.fields.disabled = false;
    this.groupChanged();
    await this.refresh();
  }

  showReceived(copy: Copy): void {
    const { ui } = this;
    ui.form.hidden = true;
    ui.received.hidden = false;
    ui.receivedEyebrow.textContent = copy.eyebrow;
    ui.receivedCopy.textContent = copy.body;
    ui.received.focus();
  }

  private message(text: string, error = false): void {
    this.ui.status.textContent = text;
    this.ui.status.classList.toggle("is-error", error);
  }

  private values(): SelectionValues {
    return {
      groupSize: this.ui.groupSize.value,
      tierId: this.tierInputs.find((input) => input.checked)?.value ?? "",
      donation: this.ui.donation.value,
      addOns: this.addOnInputs.map((input) => ({
        id: input.name,
        quantity: input.value,
      })),
    };
  }

  private contact(): Contact {
    const { ui, config } = this;
    const name = ui.name.value.trim();
    if (name.length === 0) {
      throw new BookingError("Please enter your name.");
    }
    const heardAboutUs = config.referrals.find(
      (r) => r.value === ui.heardAboutUs.value,
    )?.value;
    if (!heardAboutUs) {
      throw new BookingError("Please tell us how you heard about us.");
    }
    const phone = ui.phone.value.trim();
    return {
      name,
      email: ui.email.value.trim(),
      phone: phone.length > 0 ? phone : undefined,
      heardAboutUs,
    };
  }

  private groupChanged(): void {
    const solo = this.ui.groupSize.value === "1";
    this.ui.interestOffer.hidden = !solo;
    if (!solo && this.interestMode) {
      this.setInterestMode(false);
      return;
    }
    this.update();
  }

  private setInterestMode(on: boolean): void {
    const { ui } = this;
    this.interestMode = on;
    ui.priced.hidden = on;
    ui.summary.hidden = on;
    ui.submit.textContent = on ? INTEREST_SUBMIT_LABEL : SUBMIT_LABEL;
    ui.note.textContent = on ? NOTE_INTEREST : NOTE_PAYMENT;
    ui.interestToggle.textContent = on
      ? "Choose a place instead"
      : "Register your interest instead";
    ui.interestToggle.setAttribute("aria-pressed", String(on));
    this.update();
  }

  /** Repaints tier states for the group size and live availability, then the total. */
  private update(): void {
    const { ui, config } = this;
    const size = Number(ui.groupSize.value);
    for (const input of this.tierInputs) {
      const tier = config.event.price.tiers.find((t) => t.id === input.value);
      const wrapper = input.closest("label");
      const status = wrapper?.querySelector(".tier-status");
      if (!tier || !wrapper || !status) {
        continue;
      }
      const live = this.availability?.find((a) => a.tierId === tier.id);
      const closed = tier.soldOut || live?.soldOut === true;
      const tooSmall = tier.seats < size;
      input.disabled = !live || closed || tooSmall;
      wrapper.classList.toggle("unavailable", input.disabled);
      status.textContent = closed
        ? "Sold out"
        : tooSmall
          ? "Too small for your group"
          : !live
            ? "Availability not confirmed"
            : this.seatsLabel(tier.seats, live.remaining);
      if (input.disabled) {
        input.checked = false;
      }
    }
    if (!this.tierInputs.some((input) => input.checked)) {
      const first = this.tierInputs.find((input) => !input.disabled);
      if (first) {
        first.checked = true;
      }
    }

    if (this.interestMode) {
      ui.selectionStatus.textContent = "";
      ui.submit.disabled = this.busy;
      return;
    }
    let basket: Basket;
    try {
      basket = selection(config, this.values(), this.availability);
    } catch (error) {
      ui.total.textContent = "";
      ui.submit.disabled = true;
      ui.selectionStatus.textContent =
        error instanceof BookingError ? error.message : "";
      return;
    }
    ui.total.textContent = this.money.format(basket.total / 100);
    ui.submit.disabled = this.busy || this.refreshing;
    ui.selectionStatus.textContent = "";
  }

  private seatsLabel(seats: number, remaining: number | null): string {
    const room =
      seats === 1 ? "One person, paired with another" : `Up to ${seats} people`;
    if (remaining !== null && remaining <= FEW_LEFT) {
      return `${room} · only ${remaining} left`;
    }
    return room;
  }

  private async refresh(): Promise<void> {
    const { ui, config } = this;
    if (this.busy || this.refreshing) {
      return;
    }
    this.refreshing = true;
    ui.submit.disabled = true;
    ui.retry.disabled = true;
    this.message("Checking current availability…");
    try {
      this.availability = await fetchAvailability(config.event.slug);
      this.message(
        "Availability checked. Your place is confirmed after payment.",
      );
    } catch {
      this.availability = undefined;
      this.message(
        `We could not check availability. Please retry, or contact ${CONTACT_EMAIL}.`,
        true,
      );
    } finally {
      this.refreshing = false;
      ui.retry.disabled = false;
      this.update();
    }
  }

  private async submit(): Promise<void> {
    const { ui, config } = this;
    if (this.busy || this.refreshing || !ui.form.reportValidity()) {
      return;
    }
    try {
      const contact = this.contact();
      if (this.interestMode) {
        this.setBusy(true, "Sending your details…", "Sending…");
        await registerInterest({ eventSlug: config.event.slug, ...contact });
        this.showReceived(interestReceived(contact.email));
        return;
      }
      const basket = selection(config, this.values(), this.availability);
      this.setBusy(true, "Opening secure payment…", "Opening payment…");
      const session = await startCheckout({ ...basket.input, ...contact });
      window.location.assign(checkoutUrl(session.url));
    } catch (error) {
      this.message(
        error instanceof BookingError
          ? error.message
          : "Could not open payment. Please try again.",
        true,
      );
      ui.status.focus();
      this.setBusy(false);
    }
  }

  private setBusy(busy: boolean, status?: string, label?: string): void {
    const { ui } = this;
    this.busy = busy;
    ui.fields.disabled = busy;
    ui.submit.disabled = busy;
    ui.retry.disabled = busy;
    if (status) {
      this.message(status);
    }
    ui.submit.textContent =
      label ?? (this.interestMode ? INTEREST_SUBMIT_LABEL : SUBMIT_LABEL);
    if (!busy) {
      this.update();
    }
  }
}

/**
 * Wires the registration form to the shared VY backend. Handles the two ways
 * Stripe hands a visitor back: `?booked=1` after payment and `?top=1` after
 * cancelling, both of which arrive on the mirrored event path.
 */
export function mountBooking(): void {
  const ui = findForm();
  let config: BookingConfig;
  try {
    config = loadBookingConfig();
  } catch {
    ui.status.textContent = `The booking form could not load. Please reload the page or contact ${CONTACT_EMAIL}.`;
    ui.status.classList.add("is-error");
    return;
  }
  const booking = new Booking(ui, config);
  const params = new URLSearchParams(window.location.search);
  const returned = params.has("booked") || params.has("top");
  if (returned) {
    history.replaceState(null, "", "/#register");
  }
  if (params.has("booked")) {
    booking.showReceived(BOOKED);
    scrollToRegister();
    return;
  }
  void booking.start();
  if (returned) {
    scrollToRegister();
  }
}
